import { Purchases } from "../constants/Purchases";
import { formatPrice } from "../utilites/util";
import { IInstallmentsPriceForTable, IProduct, ITpackage, Price } from "../entities/product/types";
import AbstractProductDecorator from "./AbstractProductDecorator";
import _ from "lodash";
import { MIN_DEVICE_PRICE, MIN_DEVICE_PRICE_24, MIN_DEVICE_PRICE_36 } from "../constants/MinimumDevicePrice";


export default class WithPriceDecorator extends AbstractProductDecorator {

  constructor(product: IProduct) {
    super(product);
    this.setPrices(this.getDefaultPrices());
  }

  private getDefaultPrices() {
    if (typeof this.product.assignedTariffPackage !== 'undefined'
      && Object.keys(this.product.assignedTariffPackage).length
    ) {
      return this.product.assignedTariffPackage.prices;
    }

    return this.product.productPrices;
  }

  getCosmosInstallmentsFromUrl() {
    if (!window.location.search) return;
    const params = new URLSearchParams(window.location.search);
    const cosmosInstallmentsParam = params.get("cosmosInstallments");
    return cosmosInstallmentsParam;
  }

  setPrices(prices: any) {
    const numberOfCosmosInstallments = Number(this.getCosmosInstallmentsFromUrl()) || 0;
    const cosmosObject = this.getCosmosItem(prices.cosmos, numberOfCosmosInstallments);
    const subsidyObject = this.getSubsidyItem(prices.subsidy, numberOfCosmosInstallments);
    const extractedPrices = {
      cosmos: cosmosObject,
      subsidy: subsidyObject
    };

    const minDevicePrice =
      numberOfCosmosInstallments === 24
        ? MIN_DEVICE_PRICE_24
        : numberOfCosmosInstallments === 36
          ? MIN_DEVICE_PRICE_36
          : MIN_DEVICE_PRICE;

    const cosmos = this.getPurchasePrices(Purchases.COSMOS, extractedPrices);
    const subsidy = this.getPurchasePrices(Purchases.SUBSIDY, extractedPrices);
    const displayCreditPrice = cosmos ? cosmos.price : 0;
    const displayPrice = subsidy ? subsidy.price : 0;
    const displayCreditRate = cosmos && typeof cosmos.installments !== 'undefined' ? cosmos.installments : 0;
    const ebillDiscount = this.product.ebillDiscount ? this.product.ebillDiscount / 100 : 0;
    const greenVoucherDiscount = this.product.greenVoucherDiscount ? this.product.greenVoucherDiscount / 100 : 0;
    const devicePriceWithEbillDiscount = (Number(displayCreditPrice) - ebillDiscount) < minDevicePrice ? minDevicePrice : Number(displayCreditPrice) - ebillDiscount;
    const devicePriceWithGreenVoucherDiscount = (Number(displayCreditPrice) - greenVoucherDiscount) < minDevicePrice ? minDevicePrice : Number(displayCreditPrice) - greenVoucherDiscount;
    const devicePriceWithEbillDiscountAndGreenVoucherDiscount = (Number(displayCreditPrice) - ebillDiscount - greenVoucherDiscount) < minDevicePrice ? minDevicePrice : Number(displayCreditPrice) - ebillDiscount - greenVoucherDiscount;


    const displayCreditPriceInRate = displayCreditRate && displayCreditPrice ? formatPrice(
      displayCreditPrice / displayCreditRate
    ) : 0;

    const displayCreditOldPriceInRate = displayCreditRate && cosmos.priceOld ? formatPrice(
      cosmos.priceOld / displayCreditRate
    ) : 0;

    const displayCreditPriceInRateWithEbillDiscount = ebillDiscount ? formatPrice(
      devicePriceWithEbillDiscount / displayCreditRate
    ) : displayCreditPriceInRate;

    const displayCreditPriceInRateWithGreenVoucherDiscount = greenVoucherDiscount ? formatPrice(
      devicePriceWithGreenVoucherDiscount / displayCreditRate
    ) : 0;

    const displayCreditPriceInRateWithEbillDiscountAndGreenVoucherDiscount = (ebillDiscount && greenVoucherDiscount) ? formatPrice(
      devicePriceWithEbillDiscountAndGreenVoucherDiscount / displayCreditRate
    ) : displayCreditPriceInRateWithGreenVoucherDiscount || displayCreditPriceInRateWithEbillDiscount || displayCreditPriceInRate;

    const displaySubsidyDiscountPrice = (subsidy?.discounts) ? formatPrice(Number(subsidy.discounts[0].price)) : 0;

    const displaySubsidyDiscountPercentage = (subsidy?.discounts) ? `${subsidy.discounts[0].value}${subsidy.discounts[0].unit}` : 0;

    const greenVoucherHighlightedTotalDiscount = greenVoucherDiscount && cosmos?.priceOld ? formatPrice(
      Number(cosmos.priceOld) - Number(cosmos.price) + (greenVoucherDiscount / 100)
    ) : greenVoucherDiscount;

    const greenVoucherHighlightedTotalDiscountWithEbillDiscount = (greenVoucherDiscount && cosmos?.priceOld && ebillDiscount) ? formatPrice(
      (Number(cosmos.priceOld) * displayCreditRate - Number(cosmos.price) * displayCreditRate) + greenVoucherDiscount + ebillDiscount
    ) : greenVoucherDiscount;

    const totalDiscountDifference = displayCreditRate && cosmos.priceOld
      ? Math.round(cosmos.priceOld - displayCreditPrice) + greenVoucherDiscount
      : greenVoucherDiscount;

    this.product.prices = {
      displayCreditPrice: formatPrice(displayCreditPrice),
      displayPrice: formatPrice(displayPrice),
      displayCreditRate,
      displayCreditPriceInRate,
      displayCreditOldPriceInRate,
      displayInstallmentsPriceForTable: this.calculateInstallmentsPriceForTable(this.product),
      displayCreditPriceInRateWithEbillDiscount,
      displayCreditPriceInRateWithGreenVoucherDiscount,
      displayCreditPriceInRateWithEbillDiscountAndGreenVoucherDiscount,
      displaySubsidyDiscountPrice,
      displaySubsidyDiscountPercentage,
      greenVoucherHighlightedTotalDiscount,
      greenVoucherHighlightedTotalDiscountWithEbillDiscount,
      totalDiscountDifference
    };
  }

  getCosmosItem(cosmos: Price[], cosmosInstallmentsParam?: any): Price | void {
    if (!Array.isArray(cosmos)) return cosmos;
    const priceWith24Installments = cosmos.find((item:any) => item.installments === 24);
    const priceWithParamInstallment = cosmos.find((item:any) => item.installments === Number(cosmosInstallmentsParam));
    if (cosmosInstallmentsParam && !isNaN(Number(cosmosInstallmentsParam)) && Number(cosmosInstallmentsParam) > 0) {
      return priceWithParamInstallment || priceWith24Installments;
    }
    return priceWith24Installments;
  }


  getSubsidyItem(subsidy: Price[], cosmosInstallmentsParam?:any): Price | void {
    if (!Array.isArray(subsidy)) return subsidy;
    const priceWith0Installments = subsidy.find((item:any) => item.installments === 0);
    const priceWithParamInstallment = subsidy.find((item:any) => item.installments === Number(cosmosInstallmentsParam));

    if (cosmosInstallmentsParam && Number(cosmosInstallmentsParam)) {
      return priceWithParamInstallment || priceWith0Installments;
    }
    return priceWith0Installments;
  }

  getPurchasePrices(purchase: string, prices: any) {
    return Object.keys(prices).length && typeof prices[purchase] !== 'undefined' ?
      prices[purchase] : null;
  }

  calculateInstallmentsPriceForTable(product: IProduct): IInstallmentsPriceForTable[] {
    const prices: IInstallmentsPriceForTable[] = [];
    let uniqInstallments: number[] = [];
    let fullPrice = NaN;

    // If we find TPs we will not consider other data sources for prices
    if (product.tariffPackages.length) {
      // In Note: According to instructions from the BFF team, we need to use the first available TP tariffPackages[0] and cosmos[0]
      const cosmosApplicableTp: ITpackage | undefined = product.tariffPackages.find((tp: any) => _.has(tp, "prices.cosmos") && tp.prices.cosmos.length);

      // If we find applicable TP than we will use data from there
      if (cosmosApplicableTp) {
        uniqInstallments = _.uniqBy(cosmosApplicableTp.prices.cosmos, "installments").map((price: any) => price.installments);
        fullPrice = Array.isArray(cosmosApplicableTp.prices.cosmos) ? Number(cosmosApplicableTp.prices.cosmos[0].price) : Number(cosmosApplicableTp.prices.cosmos.price);
      } else {
        return prices;
      }
    }

    // If we can`t find applicable TP then we will try to find data in product.productPrices.cosmos part of response
    if (!uniqInstallments.length && product.productPrices.cosmos && product.productPrices.cosmos.length) {
      uniqInstallments = _.uniqBy(product.productPrices.cosmos, "installments").map((price: any) => price.installments);
      fullPrice = Array.isArray(product.productPrices.cosmos) ? Number(product.productPrices.cosmos[0].price) : Number(product.productPrices.cosmos.price);
    }

    if (uniqInstallments && fullPrice) {
      uniqInstallments.forEach((installment: number) => {
        prices.push({
          numberOfInstallments: installment,
          installmentPrice: fullPrice / installment,
          fullPrice: fullPrice
        });
      });

      return _.orderBy(prices, ['numberOfInstallments'], ['asc']);
    }

    return prices;
  }

}
